import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { Price } from "@/lib/currency";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { EnquiryModal } from "@/components/EnquiryModal";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { JAWA_SERVICES } from "@/data/content";

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

const ItineraryCard = ({ p }) => (
  <a
    href={`/shop/p/${p._id}`}
    data-testid={`itinerary-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group flex h-full flex-col overflow-hidden border border-jawa-espresso/10 bg-white transition-colors hover:border-jawa-turquoise/60"
  >
    <div className="relative h-44 overflow-hidden">
      <img src={p.image_url} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
      {p.checkout === "enquiry" ? (
        <span className="absolute left-4 top-4 bg-jawa-gold px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-jawa-nearblack">Quoted for you</span>
      ) : (
        <span className="absolute left-4 top-4 bg-jawa-coral px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white">Book online</span>
      )}
    </div>
    <div className="flex flex-1 flex-col p-6">
      <h3 className="font-serif text-xl font-medium text-jawa-espresso">{p.title}</h3>
      <p className="mt-2 line-clamp-4 flex-1 text-xs leading-relaxed text-jawa-espresso/60">{p.description}</p>
      <div className="mt-4 flex items-center justify-between border-t border-jawa-stone pt-4">
        <span className="font-serif text-2xl font-medium text-jawa-espresso" data-testid={`itinerary-price-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          {p.checkout === "enquiry" && <span className="mr-1 text-sm">from</span>}
          <Price amount={p.price} from={p.currency || "USD"} />
        </span>
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-coral group-hover:text-jawa-espresso">
          {p.checkout === "enquiry" ? "Enquire" : "Book"} <ArrowUpRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </div>
  </a>
);

export default function ServicesPage() {
  const [directory, setDirectory] = useState([]);
  const [itineraries, setItineraries] = useState([]);
  const [enquiry, setEnquiry] = useState(null);

  useEffect(() => {
    api.get("/topics?audience=general").then((res) => setDirectory(res.data)).catch(() => setDirectory([]));
    api.get("/products").then((r) => setItineraries(r.data.filter((p) => p.category === "service"))).catch(() => {});
  }, []);

  const scrollTo = (L) => {
    const el = document.getElementById(`dir-${L}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="bg-jawa-ivory" data-testid="services-page">
      <Nav />
      <Seo
        title="Services — Book JÀWÀ Support & 1-2-1 Consultations"
        description="Book real JÀWÀ services: 1-2-1 consultations, relocation starter packs, itinerary planning and an A–Z directory of trusted local services."
      />
      <main className="mx-auto max-w-7xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <SectionHeading overline="JÀWÀ Services" title="Arrive. Settle. Belong." />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-jawa-espresso/70">
            Book trusted JÀWÀ+ services — curated for returnees, tourists, investors and expats.
            Tell us what you need; we take care of the rest.
          </p>
        </FadeUp>

        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="jawa-services-grid">
          {JAWA_SERVICES.map((s, i) => (
            <FadeUp key={s.name} delay={i * 0.05}>
              <article
                className="group flex h-full flex-col overflow-hidden border border-jawa-stone bg-white"
                data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl font-medium tracking-tight text-jawa-espresso">{s.name}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-jawa-espresso/65">{s.text}</p>
                  {s.mode === "enquiry" ? (
                    <button
                      onClick={() => setEnquiry({ journey: "JÀWÀ Services", title: s.name, priceNote: s.price_note })}
                      data-testid={`service-book-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="mt-5 inline-flex w-fit items-center gap-1.5 bg-jawa-espresso px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta"
                    >
                      {s.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <Link
                      to={s.href}
                      data-testid={`service-book-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="mt-5 inline-flex w-fit items-center gap-1.5 bg-jawa-espresso px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory transition-colors hover:bg-jawa-terracotta"
                    >
                      {s.cta} <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </article>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-20">
          <SectionHeading overline="Bespoke itinerary planning" title="Trips planned around you." />
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-jawa-espresso/60">
            Personalised itineraries with recommended attractions, dining, practical timings and booking links.
            Fixed-price tiers book online instantly; VIP Planning is quoted first. Flights, accommodation, meals,
            tickets and transport are not included unless your plan explicitly says so.
          </p>
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-testid="itineraries-grid">
            {itineraries.map((p) => (
              <ItineraryCard key={p._id} p={p} />
            ))}
          </div>
          <p className="mt-6 max-w-2xl text-xs leading-relaxed text-jawa-espresso/50">
            Every itinerary includes a set number of revision rounds (stated on each tier) — enough to perfect the
            plan without open-ended scope. Airport pickup, airport concierge, transport, accommodation and
            experiences are available as separate add-ons or combined arrangements.
          </p>
        </FadeUp>

        <FadeUp className="mt-28">
          <SectionHeading overline="The A–Z JÀWÀ Directory" title="Everything else, A to Z." />
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-jawa-espresso/60">
            Free resources and links for diasporans relocating to, visiting or investing in Lagos —
            some offered by trusted partners.
          </p>
        </FadeUp>

        <div className="sticky top-[72px] z-30 mt-10 border-y border-jawa-stone bg-jawa-ivory/95 py-4 backdrop-blur-md" data-testid="directory-letter-index">
          <div className="flex flex-wrap gap-x-3 gap-y-2">
            {LETTERS.map((L) => {
              const has = directory.some((t) => (t.letter || "").toUpperCase() === L);
              return (
                <button
                  key={L}
                  onClick={() => has && scrollTo(L)}
                  disabled={!has}
                  data-testid={`dir-letter-${L}`}
                  className={`font-sans text-sm uppercase transition-colors ${
                    has
                      ? "cursor-pointer font-bold text-jawa-turquoise underline decoration-jawa-turquoise/40 underline-offset-4 hover:text-jawa-espresso"
                      : "cursor-default text-jawa-nearblack/30"
                  }`}
                >
                  {L}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-12 space-y-12" data-testid="directory-list">
          {LETTERS.filter((L) => directory.some((t) => (t.letter || "").toUpperCase() === L)).map((L) => (
            <section key={L} id={`dir-${L}`} className="scroll-mt-40" data-testid={`dir-section-${L}`}>
              <span className="font-serif text-4xl font-medium text-jawa-gold">{L}</span>
              <div className="mt-4 divide-y divide-jawa-stone border-y border-jawa-stone">
                {directory
                  .filter((t) => (t.letter || "").toUpperCase() === L)
                  .map((t) => (
                    <div key={t._id || t.title} className="flex flex-wrap items-baseline justify-between gap-3 py-5" data-testid={`directory-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      <div className="max-w-2xl">
                        <h3 className="font-serif text-xl font-medium text-jawa-espresso">{t.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-jawa-espresso/65">{t.summary}</p>
                      </div>
                      {t.link ? (
                        <a
                          href={t.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`directory-cta-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                        >
                          {t.cta || "Learn more"} <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      ) : (
                        <button
                          onClick={() => setEnquiry({ journey: "A–Z Directory", title: t.title })}
                          data-testid={`directory-cta-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-turquoise transition-colors hover:text-jawa-espresso"
                        >
                          {t.cta || "Enquire"} <ArrowUpRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
              </div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
      {enquiry && (
        <EnquiryModal
          journey={enquiry.journey}
          title={enquiry.title}
          variant="service"
          priceNote={enquiry.priceNote}
          onClose={() => setEnquiry(null)}
          testid="service-enquiry-modal"
        />
      )}
    </div>
  );
}
